import React, { useCallback, ReactNode } from 'react';
import { useNavigate, useLocation, useMatch } from 'react-router-dom';
import { useQuery } from "@apollo/client/react";
import { injectIntl, WrappedComponentProps } from 'react-intl';

import SideBar from '../../sidenav/containers/SideBar';
import Snackbar from '../components/Snackbar';
import MainRoutes from '../components/MainRoutes';
import Loader from '../../../components/material/Loader';
import VersionMismatchDialog from '../../version/components/VersionMismatchDialog';
import NotFoundPage from '../components/NotFoundPage';

import { clientAndAvailableAddonsQuery } from '../../user/graphql';
import { Client } from '../../client/Model';
import { Addon } from 'addons/Admin/models/Addon';
import Auth from '../../../Auth';

interface MainPageProps extends WrappedComponentProps {
  navCollapsed: boolean;
  showNewVersionDialog: boolean;
  navigationNotFound: boolean;
}

interface QueryData {
  client: Client;
  availableAddons: Addon[];
}

const BlockUI: React.FC<{ blocking: boolean; children: ReactNode }> = ({ blocking, children }) => {
  return (
    <div className="full-height" style={{ position: 'relative' }}>
      {blocking && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loader />
        </div>
      )}
      {children}
    </div>
  );
};

const MainPage: React.FC<MainPageProps> = ({
  navCollapsed,
  showNewVersionDialog,
  navigationNotFound,
  intl,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const match = useMatch('*');

  const collapsedClass = navCollapsed ? 'nav-collapsed' : '';
  const brand = <>{intl.formatMessage({ id: 'appName' })}</>;

  const { data, loading } = useQuery<QueryData>(clientAndAvailableAddonsQuery, {
    variables: { clientId: Auth.getJWT().clientId, userId: Auth.getJWT().id },
    fetchPolicy: 'network-only',
  });

  const addons = data?.availableAddons || [];

  const handleGoBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  return (
    <BlockUI blocking={loading}>
      <div id="app-inner">
        <div className={`full-height fixed-header sidebar-md nav-behind theme-light ${collapsedClass}`}>
          <Snackbar {...( {} as any )} />
          <VersionMismatchDialog show={showNewVersionDialog} />
          <div className="main-app-container">
            <SideBar
              onBrand={() => {}}
              path={location.pathname}
              brand={brand}
              hasAddons={addons.length > 0}
            />

            <section id="page-container" className="app-page-container">
              <div className="app-content-wrapper">
                <div className="app-content">
                  <div className="full-height">
                    {navigationNotFound && <NotFoundPage onClick={handleGoBack} />}
                    {!navigationNotFound && !loading && (
                      <MainRoutes
                        matchUrl={match?.pathname || '/'}
                        hasAddons={addons.length > 0}
                      />
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </BlockUI>
  );
};

export default injectIntl(MainPage);
